import { useMemo, useState, type MouseEvent } from "react";
import CalendarIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  IconButton,
  Button as MuiButton,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { Promisable } from "type-fest";

import type { Dayjs, TimeRangeOnDate } from "@thalamos/common";

import {
  formatRequestSummary,
  unusableRanges,
} from "../../RequestedAvailability/requestedAvailability.js";
import { RequestedAvailabilityPicker } from "../../RequestedAvailability/RequestedAvailabilityPicker.js";

interface AvailabilityFilterPopoutProps {
  value: TimeRangeOnDate[];
  onChange: (ranges: TimeRangeOnDate[]) => Promisable<void>;
  /** UK wall-clock now; the week the picker opens on and what counts as past. */
  now: Dayjs;
}

/**
 * Wide enough for seven columns of a quarter-hour grid. A Popover shrink-wraps
 * its content and the grid's columns are fractional, so without an explicit
 * width it collapses onto the time gutter.
 */
const POPOVER_WIDTH = 720;

export function AvailabilityFilterPopout({
  value,
  onChange,
  now,
}: AvailabilityFilterPopoutProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const hasUnusable = useMemo(() => unusableRanges(value).length > 0, [value]);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const picker = (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Filter by Availability
        </Typography>
        <IconButton size="small" onClick={handleClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <RequestedAvailabilityPicker
        value={value}
        onChange={onChange}
        now={now}
      />
    </>
  );

  return (
    <Box>
      <MuiButton
        variant="outlined"
        size="small"
        startIcon={<CalendarIcon />}
        onClick={handleOpen}
        data-testid="availability-filter-button"
        sx={{
          textTransform: "none",
          // Two lines of prose, not a button label, so the theme's spaced
          // capitals do not suit it.
          letterSpacing: "normal",
          justifyContent: "flex-start",
          width: "100%",
          // Fixed, so the popover anchored to this button stays put when the
          // summary it shows changes.
          minHeight: 56,
          py: 0.75,
          // The lines pin their own colours, which would otherwise survive the
          // theme's hover fill and sit dark on it.
          "&:hover .MuiTypography-root": { color: "inherit" },
        }}
      >
        <Box sx={{ flex: 1, textAlign: "left" }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "text.secondary",
            }}
          >
            Availability
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: hasUnusable ? "error.main" : undefined,
            }}
          >
            {value.length > 0 ? formatRequestSummary(value) : "No filter"}
          </Typography>
        </Box>
      </MuiButton>

      {isMobile ? (
        <Dialog open={open} onClose={handleClose} fullScreen>
          <Box sx={{ p: 2 }}>{picker}</Box>
        </Dialog>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          slotProps={{
            paper: {
              sx: { p: 2, width: POPOVER_WIDTH, maxWidth: "100%" },
            },
          }}
        >
          {picker}
        </Popover>
      )}
    </Box>
  );
}
